import HiddenInput from "@/components/form/components/HiddenInput";
import AntIcon from "@/components/icons/AntIcon";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { ValidateErrorEntity } from "@rc-component/form/es/interface";
import { App as AntApp, Button, Form } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useCreateContract, useUpdateContract, useValidateClient } from "../../../../../common/api/queries";
import { Permission } from "../../../../../common/security/enums";
import { FieldConstraintViolation, NormalizedError } from "../../../../../common/types";
import { Client } from "../../../../client/types";
import { clientToCreateUpdateContractClient } from "../../../../client/utils";
import { ProductFinancialSector } from "../../../../product/enums";
import { ContractType } from "../../../enums";
import { useContractValidation } from "../../../hooks";
import { CONTRACT_ROUTE_PATHS } from "../../../paths";
import { CreateUpdateThirdPillarContract, ThirdPillarContract } from "../../../types";
import { parseClientsViolationErrors } from "../../../utils";
import ContractFormAgentsSection from "../ContractFormAgentsSection";
import { ContractFormHeaderSection } from "../ContractFormHeaderSection";
import { ThirdPillarContractFormDataSection } from "./sections/ThirdPillarContractFormDataSection";

type Props = {
  initialContract?: ThirdPillarContract;
  onCancel?: () => void;
};

export const ThirdPillarContractForm = ({ initialContract, onCancel }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const navigate = useNavigate();

  const [client, setClient] = useState<Partial<Client> | undefined>(initialContract?.clients?.[0]);
  const [clientViolationErrors, setClientViolationErrors] = useState<Map<number, FieldConstraintViolation[]>>(
    () => new Map(),
  );

  const [form] = Form.useForm<CreateUpdateThirdPillarContract>();

  const createContract = useCreateContract();
  const updateContract = useUpdateContract();
  const handleContractValidationChange = useContractValidation({ form, contractId: initialContract?.id });

  const clientValidation = useValidateClient();

  useEffect(() => {
    if (initialContract) {
      const initialContractCopy = structuredClone(initialContract);
      form.setFieldsValue({
        ...initialContractCopy,
        historyRecords: undefined,
        clients: [],
        clientIds: [],
        institution: undefined,
        product: undefined,
        signer: undefined,
        manager: undefined,
        affiliatePartner: undefined,
        attachments: undefined,
        institutionId: initialContractCopy.institution.id,
        productId: initialContractCopy.product.id,
        clientIdentifiers: initialContractCopy.clients.map((client) => client.identifier),
        signerId: initialContractCopy.signer?.id,
        managerId: initialContractCopy.manager?.id,
        affiliatePartnerId: initialContractCopy.affiliatePartner?.id,
        gainerRecords: initialContractCopy.gainerRecords?.map((record) => ({
          id: record.id,
          optimisticLockVersion: record.optimisticLockVersion,
          startDate: record.startDate,
          endDate: record.endDate,
          gainer1Id: record.gainer1.id,
          gainer1Rate: record.gainer1Rate,
          gainer2Id: record.gainer2?.id,
          gainer2Rate: record.gainer2Rate,
          gainer3Id: record.gainer3?.id,
          gainer3Rate: record.gainer3Rate,
          gainer4Id: record.gainer4?.id,
          gainer4Rate: record.gainer4Rate,
          gainer5Id: record.gainer5?.id,
          gainer5Rate: record.gainer5Rate,
        })),
      } as CreateUpdateThirdPillarContract);
    }
  }, []);

  const handleSaveError = (error: NormalizedError) => {
    setErrorsToForm(form, "contract.attrs", error.violations);
    if (error.violations) {
      setClientViolationErrors(parseClientsViolationErrors(error.violations));
    }
  };

  const handleFormFinish = () => {
    form
      .validateFields()
      .then((values) => {
        if (clientViolationErrors.size > 0) {
          notification.error({
            title: t("common.error"),
            description: t("contract.validations.formError"),
          });
        } else {
          const processedValues = { ...values };
          processedValues.clients = client ? [clientToCreateUpdateContractClient(client)] : [];
          processedValues.clientIds = [];
          processedValues.policyHolderIndex = 0;
          delete processedValues.clientIdentifiers;

          if (initialContract) {
            updateContract.mutate(
              { id: initialContract.id, object: processedValues },
              {
                onSuccess: onCancel,
                onError: handleSaveError,
              },
            );
          } else {
            createContract.mutate(processedValues, {
              onError: handleSaveError,
              onSuccess: (data) => {
                navigate(generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: data.id }));
              },
            });
          }
        }
      })
      .catch((errors: ValidateErrorEntity) => {
        notification.error({
          title: t("common.error"),
          description: t("contract.validations.formError"),
        });
        resolveFormValidationError(errors);
      });
  };

  const handleClientChange = (client?: Partial<Client>) => {
    setClient(client);
    if (client) {
      clientValidation.mutate(
        { prefix: `clients[0]`, client: clientToCreateUpdateContractClient(client) },
        {
          onError: (error) => {
            if (error.violations) {
              setClientViolationErrors(
                new Map([[0, error.violations.map((v) => ({ ...v, fieldPath: v.fieldPath.substring(18) }))]]),
              );
            }
          },
        },
      );
    }
  };

  return (
    <Form form={form} layout="vertical" name="thirdPillarContractForm" onValuesChange={handleContractValidationChange}>
      <HiddenInput name="type" initialValue={ContractType.THIRD_PILLAR_CONTRACT} />
      <HiddenInput name="optimisticLockVersion" />
      <HiddenInput name="status" />

      <ContractFormHeaderSection
        initialContract={initialContract}
        financialSector={ProductFinancialSector.SUPPLEMENTARY_PENSION_SAVINGS}
        privilegedChangesPermission={Permission.PRIVILEGED_CHANGES_ON_VERIFIED_THIRD_PILLAR}
        client={client}
        clientViolationErrors={clientViolationErrors}
        onClientChange={handleClientChange}
        onClientViolationErrorsDelete={() => setClientViolationErrors(new Map())}
      />

      <ThirdPillarContractFormDataSection />

      <ContractFormAgentsSection
        initialContract={initialContract}
        privilegedChangesPermission={Permission.PRIVILEGED_CHANGES_ON_VERIFIED_THIRD_PILLAR}
      />

      <div className="margin-top-small">
        <Button type="primary" icon={<AntIcon type="save" />} onClick={handleFormFinish}>
          {t("common.save")}
        </Button>

        {onCancel && (
          <Button className="margin-left-tiny" onClick={onCancel} icon={<AntIcon type="close" />}>
            {t("common.cancel")}
          </Button>
        )}
      </div>
    </Form>
  );
};
